import { AsyncPipe, NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { StepService, UuidExtraInfoLoggingService } from '@lx/quote-tool/shared/services';
import { QuoteToolStore } from '@lx/quote-tool/shared/store';
import { CourseQuoteInput, QuoteInput, StudentDetails } from '@lx/quote-tool/shared/types';
import { FormErrorMessageComponent } from '@lx/quote-tool/shared/ui/components';
import { ResidenceCountry } from '@lx/shared/data-access/destination';
import { CourseType } from '@lx/shared/data-access/types';
import { GoogleAnalyticsClickEventDirective, GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { DateFormat, DateFormatService } from '@lx/shared/i18n';
import { ButtonComponent } from '@lx/shared/ui/button';
import { addWeeks, getDateFromString } from '@lx/shared/utils';
import { AccordionModule } from 'primeng/accordion';
import { ButtonModule } from 'primeng/button';
import { BehaviorSubject, Observable, Subject, combineLatest, takeUntil } from 'rxjs';
import { PriceRequestFormGoogleAnalyticsEvent } from '../../../../google-analytics';
import { PriceRequestExperimentService } from '../../../../shared/experiments';
import { PriceRequestFormStore } from '../../../../store/store';
import { PriceRequestCourseFormComponent } from './course-form/price-request-course-form.component';
import { PriceRequestStudentFormComponent } from './student-form/price-request-student-form.component';

@Component({
  standalone: true,
  selector: 'lx-price-request-form',
  templateUrl: './price-request-form.component.html',
  styleUrls: ['./price-request-form.component.scss'],
  imports: [
    AccordionModule,
    NgIf,
    PriceRequestStudentFormComponent,
    PriceRequestCourseFormComponent,
    AsyncPipe,
    NgTemplateOutlet,
    ButtonComponent,
    NgClass,
    ButtonModule,
    FormErrorMessageComponent,
    GoogleAnalyticsClickEventDirective,
  ],
})
export class PriceRequestFormComponent implements OnInit, OnDestroy {
  @ViewChild('priceRequestForm') priceRequestFormRef: ElementRef<HTMLElement>;
  activeIndex: number | undefined = 0;

  quoteInput$: Observable<QuoteInput> | undefined;
  courseType$: Observable<CourseType> | undefined;
  studentDetails$: Observable<StudentDetails> | undefined;

  quoteInputSummary = '';

  filledStudentDetails = false;
  studentDetailsContactInfoSummary = '';
  studentDetailsAddressSummary = '';

  quoteInputSummaryVisible = false;
  studentDetailsSummaryVisible = false;

  formError = false;

  validationRequests = 0;

  courseFormValid$ = new BehaviorSubject(false);
  studentFormValid$ = new BehaviorSubject(false);
  withPriceRequestInlineCtaExperimentActive = false;

  readonly formIndexes = {
    courseForm: 0,
    studentForm: 1,
  };

  protected readonly PriceRequestFormGoogleAnalyticsEvent = PriceRequestFormGoogleAnalyticsEvent;

  private closeSubscription$: Subject<void> = new Subject<void>();
  private closeSubscriptions$: Subject<void> = new Subject<void>();

  private readonly dateFormatService = inject(DateFormatService);
  private readonly priceRequestStore = inject(PriceRequestFormStore);
  private readonly quoteToolStore = inject(QuoteToolStore);
  private readonly stepService = inject(StepService);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly uuidExtraLoggingService = inject(UuidExtraInfoLoggingService);
  private readonly priceRequestExperimentService = inject(PriceRequestExperimentService);

  ngOnInit(): void {
    this.priceRequestStore.initUuidExtraLogging();
    this.quoteInput$ = this.priceRequestStore.quoteInput$;
    this.courseType$ = this.priceRequestStore.quoteCourseType$;
    this.studentDetails$ = this.priceRequestStore.studentDetails$;

    this.googleAnalyticsService.trackViewEvent(PriceRequestFormGoogleAnalyticsEvent.ViewPriceRequestForm);

    combineLatest([this.priceRequestStore.quoteInput$, this.priceRequestStore.quoteCourseType$])
      .pipe(takeUntil(this.closeSubscriptions$))
      .subscribe(([quoteInput, courseType]: [QuoteInput, CourseType]) => {
        if (!quoteInput || !courseType) {
          this.courseFormValid$.next(false);
        } else {
          // Calculate the validity of the step.
          const { languageCode, countryCode, destinationCity, tuitionWeeks, startDate, accommodationType } = quoteInput;

          // The step is valid if language, country, city, weeks, start date and accommodation are selected
          this.courseFormValid$.next(Boolean(languageCode && countryCode && destinationCity && tuitionWeeks > 0 && startDate && accommodationType));
        }
      });

    // Prepare the summary of the quote input
    this.prepareQuoteInputSummary();

    // Prepare summary of student details
    this.prepareStudentDetailsSummary();

    this.withPriceRequestInlineCtaExperimentActive = this.priceRequestExperimentService.isPriceRequestInlineCtaExperimentActive();
  }

  ngOnDestroy(): void {
    this.closeSubscription$.next();
  }

  displayStudentForm() {
    this.activeIndex = this.formIndexes.studentForm;

    this.showSummary(this.formIndexes.courseForm);
    this.hideSummary(this.formIndexes.studentForm);
  }

  displayCourseForm() {
    this.activeIndex = this.formIndexes.courseForm;

    this.showSummary(this.formIndexes.studentForm);
    this.hideSummary(this.formIndexes.courseForm);
  }

  editForm($event: Event, index: number) {
    this.activeIndexChange(index);
    $event.preventDefault();
    $event.stopPropagation();
  }

  sendEnquiry() {
    // Track the click event here instead of using the directive because the button is gone as soon as we click on it because
    // we navigate to the next step
    this.googleAnalyticsService.trackClickEvent(PriceRequestFormGoogleAnalyticsEvent.QuoteClickSend);

    combineLatest([this.priceRequestStore.quoteInput$, this.priceRequestStore.studentDetails$, this.priceRequestStore.quoteCourseType$])
      .pipe(takeUntil(this.closeSubscriptions$))
      .subscribe(([quoteInput, studentDetails, courseType]) => {
        this.stepService.updatePartialRequest<QuoteInput>({
          ...quoteInput,
          courses: [
            <CourseQuoteInput>{
              courseType,
              weeks: quoteInput.tuitionWeeks,
              startDate: quoteInput.startDate,
            },
          ],
        });
        this.stepService.setPartialStudentDetails({ ...studentDetails });

        // Close the subscription
        this.closeSubscriptions$.next();

        // Prevent form submission if any of the required fields are empty.
        if (!this.studentFormValid$.value) {
          this.validationRequests = 1;
          this.formError = !this.formError;
          this.priceRequestFormRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
          this.uuidExtraLoggingService.logMessage(`was prevented from submitting the price request form x${this.validationRequests}.`);
          return;
        }

        this.uuidExtraLoggingService.logMessage('successfully submitted the price request form.');

        // The next step is the SEND_QUOTE_TO_EMAIL, and it is the one in charge of calling to /leads endpoint
        this.stepService.navigateToNextStep();
      });
  }

  activeIndexChange(index: number | number[]) {
    if (index === null || index === undefined) {
      this.activeIndex = undefined;
    } else if (typeof index === 'number') {
      // we know this accordion is not multiple, so only one selection is possible
      this.activeIndex = index;
    } else {
      // this should not happen
      this.activeIndex = index[0];
    }

    switch (this.activeIndex) {
      case this.formIndexes.courseForm:
        this.displayCourseForm();
        break;
      case this.formIndexes.studentForm:
        this.displayStudentForm();
        break;
      default:
        break;
    }

    this.priceRequestFormRef?.nativeElement?.scrollIntoView({ behavior: 'smooth' });
  }

  showSummary(index: number) {
    switch (index) {
      case this.formIndexes.courseForm:
        this.quoteInputSummaryVisible = true;
        break;
      case this.formIndexes.studentForm:
        this.studentDetailsSummaryVisible = true;
        break;
      default:
        break;
    }
  }

  hideSummary(index: number) {
    switch (index) {
      case this.formIndexes.courseForm:
        this.quoteInputSummaryVisible = false;
        break;
      case this.formIndexes.studentForm:
        this.studentDetailsSummaryVisible = false;
        break;
      default:
        break;
    }
  }

  private prepareQuoteInputSummary() {
    combineLatest([this.priceRequestStore.quoteInput$, this.priceRequestStore.quoteCourseType$])
      .pipe(takeUntil(this.closeSubscription$))
      .subscribe(([quoteInput, courseType]: [QuoteInput, CourseType]) => {
        this.quoteInputSummary = '';

        if (!quoteInput || !courseType) {
          return;
        }

        const { country, destinationCity, startDate, tuitionWeeks } = quoteInput;
        if (country && tuitionWeeks && startDate) {
          this.quoteInputSummary += courseType.name;
          this.quoteInputSummary += ` | ${country.name} `;
          this.quoteInputSummary += destinationCity ? ` | ${destinationCity.name} ` : '';
          const endDate = addWeeks(getDateFromString(startDate), tuitionWeeks);
          const formattedStartDate = this.dateFormatService.formatDate(startDate, DateFormat.FULL_MONTH_NAME);
          const formattedEndDate = this.dateFormatService.formatDate(endDate, DateFormat.FULL_MONTH_NAME);
          this.quoteInputSummary += ` | ${formattedStartDate} - ${formattedEndDate}`;
        }
      });
  }

  private prepareStudentDetailsSummary() {
    combineLatest([this.studentDetails$, this.quoteToolStore.countriesOfResidence$])
      .pipe(takeUntil(this.closeSubscription$))
      .subscribe(([studentDetails, countriesOfResidence]: [StudentDetails, ResidenceCountry[]]) => {
        if (!studentDetails) {
          return;
        }

        const { firstName, lastName, email, phoneNumber, postcode, countryOfResidence } = studentDetails;
        this.filledStudentDetails = Boolean(firstName && lastName && email && phoneNumber && postcode && countryOfResidence);
        this.studentDetailsContactInfoSummary = email && phoneNumber ? `${email} | ${phoneNumber}` : '';

        if (postcode && countryOfResidence) {
          // Getting the name of the residence country from the list of countries of residences in the store
          const countryOfResidenceName = countriesOfResidence.find(({ code }) => code === countryOfResidence)?.name || countryOfResidence;
          this.studentDetailsAddressSummary = `${postcode} | ${countryOfResidenceName}`;
        } else {
          this.studentDetailsAddressSummary = '';
        }
      });
  }
}
